<template>
  <div>
    <div class="bg-white m-3 p-5 rounded-md relative">
      <transition name="fade">
        <div
          v-show="loading"
          class="inset-0 absolute bg-black rounded-md bg-opacity-10 w-full"
        >
          <Loader class="center-h-v" />
        </div>
      </transition>

      <div
        class="flex justify-between"
        v-if="getUserRole.toLowerCase() === 'hrmanager'"
      >
        <router-link
          v-if="!removeUnnecessaryBtn"
          :to="{ name: 'employees', params: { type: 'all' } }"
          class="flex items-center gap-2 rounded-full w-max p-2 justify-center text-sm bg-gray-200"
        >
          <img class="h-4 object-contain" src="@/assets/icons/left.png" />
          <p>Back to Employees</p>
        </router-link>
      </div>

      <div class="flex flex-wrap justify-between items-center">
        <div class="flex items-center gap-5 flex-wrap justify-center">
          <!-- Profile Picture Container -->
          <div
            class="profile-picture-container h-20 w-20"
            @mouseenter="showIcon = true"
            @mouseleave="showIcon = false"
          >
            <div
              v-if="!localUser?.ProfilePicture"
              class="w-full h-full flex items-center justify-center text-white text-3xl font-bold bg-gray-500 rounded-full"
            >
              {{ getInitials(localUser?.Fullname) }}
            </div>

            <img
              v-else
              :src="profilePicUrl"
              alt=""
              class="w-full h-full rounded-full"
            />

            <span
              class="camera-icon"
              v-if="showIcon"
              @click="openEditProfilePictureDialog"
            >
              <img
                class="h-4 w-4"
                src="@/assets/icons/camera.svg"
                alt="Camera Icon"
              />
            </span>
          </div>

          <!-- User Information -->
          <div class="text-sm flex flex-col gap-2">
            <div class="text-sm flex flex-col">
              {{ decryptDataLocal(localUser?.Fullname, decryptionKey) }}
            </div>
            <div
              class="text-sm flex flex-col py-0.5 px-2 rounded-full"
              :class="{
                'text-white': isDarkBackground(
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.EmployementStatus?.trim()?.toLowerCase(),
                    decryptionKey
                  )
                ),
                'text-black': !isDarkBackground(
                  // decryptDataLocal(
                  localUser?.generalWorkInformation?.EmployementStatus?.trim()?.toLowerCase()
                  //   decryptionKey
                  // )
                ),
                'bg-green-200':
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.EmployementStatus?.trim()?.toLowerCase(),
                    decryptionKey
                  ) === 'active',
                'bg-yellow-500':
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.EmployementStatus?.EmployementStatus?.trim()?.toLowerCase(),
                    decryptionKey
                  ) === 'probation',
                'bg-gray-200':
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.EmployementStatus?.EmployementStatus?.trim()?.toLowerCase(),
                    decryptionKey
                  ) === 'inactive',
                'bg-red-200':
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.EmployementStatus?.EmployementStatus?.trim()?.toLowerCase(),
                    decryptionKey
                  ) === 'suspended' ||
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.EmployementStatus?.EmployementStatus?.trim()?.toLowerCase(),
                    decryptionKey
                  ) === 'terminated',
              }"
              style="width: fit-content"
            >
              {{
                decryptDataLocal(
                  localUser?.generalWorkInformation?.EmployementStatus,
                  decryptionKey
                )
              }}
            </div>

            <div class="flex flex-wrap gap-5 pt-2 text-xs" v-if="localUser">
              <span class="text-blue-500 cursor-pointer">
                &#9742;{{ abbreviatedPhoneNumber }}
              </span>

              <span class="text-blue-500 cursor-pointer">
                &#9993;{{
                  decryptDataLocal(
                    localUser?.generalWorkInformation?.WorkEmail,
                    decryptionKey
                  )
                }}
              </span>
            </div>
          </div>
        </div>
        <div
          class="flex flex-col items-end gap-3"
          v-if="getUserRole.toLowerCase() === 'hrmanager'"
        >
          <VMenu :placements="bottom" :distance="10">
            <div
              v-if="!removeUnnecessaryBtn"
              class="bg-gray-200 rounded-full w-max px-4 py-2 text-sm cursor-pointer"
            >
              <p>Employee action</p>
            </div>
            <template #popper>
              <div class="flex flex-col bg-gray-100 w-32 items-center">
                <p
                  @click="showOffboard = true"
                  class="cursor-pointer hover:bg-primary hover:text-white p-2 w-full"
                >
                  Suspend
                </p>
                <p
                  class="cursor-pointer hover:bg-primary hover:text-white p-2 w-full"
                >
                  Offboard
                </p>
              </div>
            </template>
          </VMenu>
          <!-- <ProfileCompletion /> -->
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div
      class="flex lg:flex-nowrap lg:h-12 h-auto lg:justify-between justify-start divide-x-1.5 bg-white mx-3 mb-5 mt-5 items-center overflow-x-auto"
    >
      <div
        @click="changeTab(tab)"
        v-for="(tab, index) in filteredTabs"
        :key="index"
        :class="{
          'bg-primary text-white': selectedTab == tab,
        }"
        class="text-center lg:w-full w-max h-full flex font-semibold items-center justify-center hover:bg-primary hover:text-white cursor-pointer p-2"
      >
        <p style="font-size: 0.8rem !important">{{ tab }}</p>
      </div>
    </div>

    <!-- Dynamic Components -->
    <general
      :key="keys.giKey"
      v-if="selectedTab == 'General Information'"
      :currentEmployee="currentEmployee"
    />
    <job
      :key="keys.jiKey"
      v-if="selectedTab == 'Job Information'"
      :currentEmployee="currentEmployee"
    />
    <employment
      :key="keys.eeKey"
      v-if="selectedTab == 'Employment & Education'"
      :currentEmployee="currentEmployee"
    />
    <compensation
      v-if="selectedTab == 'Compensation & Benefits'"
      :key="keys.cbKey"
      :currentEmployee="localUser"
    />
    <documents
      :key="keys.docKey"
      v-if="selectedTab == 'Documents'"
      :currentEmployee="currentEmployee"
    />
    <claims
      :key="keys.claimKey"
      v-if="selectedTab == 'Claims'"
      :currentEmployee="currentEmployee"
      :claimTab="claimTab"
    />
    <assets
      :key="keys.assetKey"
      v-if="selectedTab == 'Technologies'"
      :currentEmployee="currentEmployee"
    />
    <Leave
      :key="keys.LeaveKey"
      v-if="selectedTab == 'Leave'"
      :currentEmployee="currentEmployee"
    />
     <Separation
      :key="keys.SeparationKey"
      v-if="selectedTab == 'Separation'"
      :currentEmployee="currentEmployee"
    />
    <Permissions
      :key="keys.PermissionsKey"
      v-if="selectedTab == 'Permissions'"
      :currentEmployee="currentEmployee"
    />
    <!-- offboard -->
    <offboard :showOffboard="showOffboard" @close="handleOffboardingClose" />
    <Disciplinary
      :key="keys.DisciplinaryKey"
      v-if="selectedTab==='Disciplinary'"
      :currentEmployee="currentEmployee"
    />
    <EditProfilePicture
      :close="close"
      :show-edit-profile-picture="showEditProfilePicture"
      :currentPicture="profilePicUrl"
      :placeholder="localUser?.Fullname ? localUser.Fullname[0] : ''"
    />
  </div>
</template>

<script>
import General from '@/components/employees/tabs/general.vue';
import Job from '@/components/employees/tabs/job.vue';
import Employment from '@/components/employees/tabs/employment.vue';
import Compensation from '@/components/employees/tabs/compensation.vue';
import Documents from '@/components/employees/tabs/documents.vue';
import Claims from '@/components/employees/tabs/claims.vue';
import Assets from '@/components/employees/tabs/assets.vue';
import Offboard from './offboard.vue';
import Disciplinary from "@/components/employees/tabs/disciplinary.vue"
// import ProfileCompletion from "./profileCompletion.vue";
import Leave from '@/components/employees/tabs/leaves.vue';
import Separation from "@/components/employees/tabs/separation.vue";
import { mapGetters } from 'vuex';
import axios from 'axios';
import EditProfilePicture from '@/components/employees/modals/editProfilePicture.vue';
import { viewSecuredAttachment } from '@/utils/secureAttachmentsViewer';
import { fetchDataMixin } from '@/mixins/fetchDataMixin';
import Loader from '@/components/loader/loader.vue';
import Permissions from '@/components/CompanySetups/permissions/permissions.vue';
import decryptData from '@/utils/decrypt';
export default {
  name: 'Employee',
  props: ['user', 'setLoading'],
  mixins: [fetchDataMixin],

  components: {
    General,
    Job,
    Employment,
    Compensation,
    Documents,
    Claims,
    Assets,
    Offboard,
    // ProfileCompletion,
    Leave,
    Separation,
    EditProfilePicture,
    Loader,
    Permissions,
    Disciplinary
  },

  data() {
    return {
      decryptionKey: '',
      decryptDataLocal: '',
      currentEmpId: '',
      currentEmployee: {},
      bottom: 'bottom',
      showOffboard: false,
      selectedTab: 'General Information',
      selectedAction: 'Select action',
      employees: [],
      employeeInfo: [],
      profilePicUrl: '',
      localUser: {},
      claimTab: null,
      showIcon: false,
      loading: false,
      showEditProfilePicture: false,
      tabs: [
        'General Information',
        'Job Information',
        'Employment & Education',
        'Compensation & Benefits',
        'Documents',
        'Claims',
        'Technologies',
        'Leave',
         'Separation',
        'Permissions',
        'Disciplinary'
      ],
      keys: {
        giKey: 0,
        jiKey: 1,
        eeKey: 2,
        cbKey: 3,
        docKey: 4,
        claimKey: 5,
        assetKey: 6,
        LeaveKey: 7,
        SeparationKey: 8,
        DisciplinaryKey:9
      },
    };
  },

  // beforeMount() {
  //   this.loadData();
  // },

  destroyed() {
    this.$store.commit('setUserToView', {});
  },

  created() {
    this.decryptDataLocal = decryptData;
    this.decryptionKey = this.$decryptionKey;
    this.loadData();
    this.initializeRoute();
  },

  watch: {
    selectedAction(val) {
      if (val === 'Offboard') {
        this.showOffboard = true;
      }
    },
    tabs() {
      return this.getUserRole.toLowerCase() === 'hrmanager'
        ? this.tabs.filter((tab) => tab !== 'Leave')
        : this.tabs;
    },
  },

  computed: {
    removeUnnecessaryBtn() {
      return !this.localUser || !this.$route.name;
    },

    ...mapGetters([
      'getUserRole',
      'getToken',
      'getCurrentEmployeeId',
      'getOrganization',
      'getUserToView',
      'getUser',
    ]),

    filteredTabs() {
      return this.getUserRole.toLowerCase() === 'hrmanager'
        ? this.tabs
        : [...this.tabs].filter(
            (tab) => tab !== 'Leave' && tab !== 'Permissions'
          );
    },

    abbreviatedPhoneNumber() {
      const phoneNumber =
        this.localUser?.WorkPhoneNumber || this.localUser?.PersonalPhoneNumber;
      return phoneNumber
        ? decryptData(phoneNumber, this.$decryptionKey).slice(0, 10)
        : '';
    },
  },

  methods: {
    isDarkBackground(status) {
      const darkBackgrounds = [
        'bg-green-500',
        'bg-yellow-500',
        'bg-gray-500',
        'bg-red-500',
      ];

      return darkBackgrounds.includes(status);
    },
    getInitials(fullname) {
      const decryptedName = decryptData(fullname, this.$decryptionKey);
      return (
        fullname &&
        decryptedName
          .split(' ')
          .map((name) => name[0])
          .join('')
      );
    },
    openEditProfilePictureDialog() {
      this.showEditProfilePicture = true;
    },

    showCameraIcon() {
      this.hovering = true;
    },

    hideCameraIcon() {
      this.hovering = false;
    },

    async getProfilePicUrlurl() {
      try {
        this.profilePicUrl = await viewSecuredAttachment(
          this?.localUser.ProfilePicture,
          this.getToken
        );
        return this.profilePicUrl;
      } catch (error) {
        console.error(error.message);
      }
    },

    handleOffboardingClose() {
      this.showOffboard = false;
      this.selectedAction = 'Select action';
    },

    close() {
      this.showEditProfilePicture = false;
      this.getEmployeeInfo();
    },

    changeTab(name) {
      this.selectedTab = name;
      this.keys[`${name.toLowerCase()}Key`]++;
    },

    async loadData() {
      try {
        this.loading = true;
        this.setLoading(this.loading);
        await Promise.all([
          this.getEmployees(),
          this.getEmployeeInfo(),
          this.getEmployeeJobInfo(),
        ]);
      } catch (error) {
        this.loading = false;
        console.error(error.message);
        this.get_error = error.message;
      }
    },

    initializeRoute() {
      if (this.$route.hash === '#claims') {
        this.selectedTab = 'Claims';
        this.claimTab = 'Team';
      } else {
        this.claimTab = 'Self';
      }
    },

    async getEmployees() {
      const options = { headers: { Authorization: `Bearer ${this.getToken}` } };
      const response = await axios.get(
        `${this.getOrganization}/employee/all/${this.$ApiVersion}/request`,
        options
      );
      if (response.data.StatusCode === 200) {
        this.employees = response.data.Data;
      } else {
        throw new Error(response.data.StatusMessage);
      }
    },

    async getEmployeeInfo() {
      const options = { headers: { Authorization: `Bearer ${this.getToken}` } };
      const response = await axios.get(
        `${this.getOrganization}/employee/${this.getCurrentEmployeeId}/general/${this.$ApiVersion}/info`,
        options
      );
      if (response.data.StatusCode === 200) {
        this.loading = false;
        this.setLoading(this.loading);
        response.data.Data.PersonalInformation;

        this.localUser = {
          ...this.localUser,
          ...response.data.Data.PersonalInformation,
        };
      } else {
        this.setLoading(false);
        throw new Error(response.data.StatusMessage);
      }
    },

    async getEmployeeJobInfo() {
      const options = { headers: { Authorization: `Bearer ${this.getToken}` } };
      const response = await axios.get(
        `${this.getOrganization}/employee/${this.getCurrentEmployeeId}/job/information/${this.$ApiVersion}/request`,
        options
      );

      if (response.data.StatusCode === 200) {
        this.localUser = { ...this.localUser, ...response.data.Data };

        await this.getProfilePicUrlurl();
      } else {
        throw new Error(response.data.StatusMessage);
      }
    },
  },
};
</script>

<style>
.profile-picture-container {
  position: relative;
  width: 100px; /* Set the appropriate size */
  height: 100px; /* Set the appropriate size */
  border-radius: 50%;
  background-color: #ccc; /* Set a default background color or use your image */
  cursor: pointer;
}

.profile-picture-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.camera-icon {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  cursor: pointer;
  background-color: rgba(
    0,
    0,
    0,
    0.6
  ); /* Adjust the background color and opacity to your preference */
  border-radius: 50%;
  transition: none;
}

.profile-picture-container:hover .camera-icon {
  opacity: 0.6;
}
</style>
